import { useCallback, useState } from 'react'

import type { BatchStripProps } from './batchStripTypes.ts'

/**
 * 스냅샷·구독이 붙기 전 작업 줄의 기본값.
 *
 * 🔴 **모르는 쪽이 아니라 안전한 쪽으로 기울여 둔다.**
 *   - `safety: 'unknown'` — 안전 스트림이 없으니 「연결 끊김 — 안전 미확인」이 맞다.
 *     마지막 값을 현재로 보여주지 않는다(§5-7).
 *   - `allowedCommands: []` — 서버가 아무것도 허용하지 않은 것과 같다. 낙관적 활성 금지.
 *   - `permissions` 없음 — 제어 버튼이 전부 비활성 + 사유로 나간다.
 *
 * 실물 API 가 붙으면 이 상수 대신 스냅샷 쿼리 + 구독 결과를 넣는다. 여기서 값을 지어내지 마라.
 */
export const UNKNOWN_STRIP_STATE = {
  batch: null,
  lock: { kind: 'none' },
  allowedCommands: [],
  safety: { state: 'unknown' },
} as const satisfies Pick<BatchStripProps, 'batch' | 'lock' | 'allowedCommands' | 'safety'>

export interface ShellState {
  /** 사이드바 접힘 — 216px ↔ 64px */
  navCollapsed: boolean
  toggleNav: () => void
  /** 작업 줄 이벤트 펼침. 펼치면 화면 본문이 사라지고 이벤트가 전부 차지한다(§13) */
  eventsOpen: boolean
  toggleEvents: () => void
}

/**
 * 셸 UI 상태. 서버 상태가 아니라 화면 배치 상태라 전역 스토어에 두지 않았다 —
 * 셸 하나가 쥐고 아래로 내린다. 셸이 언마운트되면 같이 사라지는 게 맞는 값들이다.
 */
export function useShellState(): ShellState {
  const [navCollapsed, setNavCollapsed] = useState(false)
  const [eventsOpen, setEventsOpen] = useState(false)

  return {
    navCollapsed,
    toggleNav: useCallback(() => setNavCollapsed((value) => !value), []),
    eventsOpen,
    toggleEvents: useCallback(() => setEventsOpen((value) => !value), []),
  }
}
